import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { Platform, type ViewStyle } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, ButtonText, Heading, HStack, Spinner, Text, VStack,
} from '@gluestack-ui/themed';

export type MeetingConfirmOptions = {
  title: string;
  message?: string;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  overlayTestID?: string;
  messageTestID?: string;
  cancelTestID?: string;
  confirmTestID?: string;
  /** When set, confirm awaits this before closing; false keeps the dialog open. */
  onConfirm?: () => Promise<boolean>;
  confirmErrorMessage?: string;
  confirmAnywayLabel?: string;
  confirmErrorTestID?: string;
  confirmAnywayTestID?: string;
};

export type MeetingProgressOptions = {
  title: string;
  lines: string[];
  pending: boolean;
  error?: string | null;
  onRetry?: () => void;
  onClose?: () => void;
};

type MeetingConfirmContextValue = {
  /** Open a two-button confirmation and resolve true (confirmed) or false (cancelled). */
  confirm: (options: MeetingConfirmOptions) => Promise<boolean>;
  showProgress: (options: MeetingProgressOptions) => void;
  hideProgress: () => void;
  /** Close any open confirm/progress without confirming (e.g. Connection lost took over). */
  dismissOpenConfirm: () => void;
};

const MeetingConfirmContext = createContext<MeetingConfirmContextValue | null>(null);

export function useMeetingConfirm() {
  const ctx = useContext(MeetingConfirmContext);
  if (!ctx) throw new Error('useMeetingConfirm must be used within MeetingConfirmProvider');
  return ctx;
}

/** No-op outside MeetingConfirmProvider (e.g. isolated guard unit tests). */
export function useDismissOpenMeetingConfirm(): () => void {
  const ctx = useContext(MeetingConfirmContext);
  return ctx?.dismissOpenConfirm ?? (() => {});
}

const VIEWPORT_SCRIM_STYLE = {
  position: 'fixed' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  minHeight: '100vh',
};

function meetingScrimHost(): HTMLElement | null {
  // The lecturer title bar is a sibling above the meeting screen. A scrim
  // inside the screen cannot cover it: the navigator card is transformed, so
  // position:fixed is trapped there. On web, attach the scrim to document.body.
  if (Platform.OS !== 'web') return null;
  if (typeof document === 'undefined' || !document.body) return null;
  return document.body;
}

function MeetingScrim({
  testID,
  children,
}: {
  testID: string;
  children: ReactNode;
}) {
  const host = meetingScrimHost();
  const scrim = (
    <Box
      // Parent-relative absolute on Center sized this dim to the start-gate
      // row (~100px). Pin to the viewport so the whole screen greys out,
      // including the lecturer title bar.
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      w="$full"
      h="$full"
      bg="rgba(0,0,0,0.45)"
      zIndex={10000}
      elevation={24}
      justifyContent="center"
      alignItems="center"
      pointerEvents="auto"
      testID={testID}
      style={VIEWPORT_SCRIM_STYLE as unknown as ViewStyle}
    >
      {children}
    </Box>
  );
  if (!host) return scrim;
  // Web only. A static import would pull react-dom into the native bundle.
  // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
  const { createPortal } = require('react-dom') as typeof import('react-dom');
  return createPortal(scrim, host);
}

/**
 * In-tree overlay host for meeting / oral-exam chrome. LiveKit video creates a
 * stacking context that hides Gluestack AlertDialog (including app-root
 * `useDialog()`). Mount this around the meeting subtree and use `confirm` /
 * `showProgress` — never inline AlertDialog inside the room.
 */
export function MeetingConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<MeetingConfirmOptions | null>(null);
  const [progress, setProgress] = useState<MeetingProgressOptions | null>(null);
  const [confirmPhase, setConfirmPhase] = useState<'idle' | 'error'>('idle');
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const optionsRef = useRef<MeetingConfirmOptions | null>(null);
  optionsRef.current = options;

  const confirm = useCallback((next: MeetingConfirmOptions) => (
    new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setProgress(null);
      setConfirmPhase('idle');
      setOptions(next);
    })
  ), []);

  const settle = useCallback((confirmed: boolean) => {
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
    setConfirmPhase('idle');
    setOptions(null);
  }, []);

  const handleConfirmPress = useCallback(async () => {
    if (!optionsRef.current) {
      return;
    }
    const { onConfirm } = optionsRef.current;

    if (confirmPhase === 'error') {
      settle(true);
      return;
    }

    if (onConfirm) {
      try {
        const ok = await onConfirm();
        if (ok) {
          settle(true);
        } else {
          setConfirmPhase('error');
        }
      } catch {
        setConfirmPhase('error');
      }
      return;
    }

    settle(true);
  }, [confirmPhase, settle]);

  const showProgress = useCallback((next: MeetingProgressOptions) => {
    setOptions(null);
    setProgress(next);
  }, []);

  const hideProgress = useCallback(() => {
    setProgress(null);
  }, []);

  const dismissOpenConfirm = useCallback(() => {
    resolverRef.current?.(false);
    resolverRef.current = null;
    setConfirmPhase('idle');
    setOptions(null);
    setProgress(null);
  }, []);

  const value = useMemo(
    () => ({
      confirm, showProgress, hideProgress, dismissOpenConfirm,
    }),
    [confirm, dismissOpenConfirm, hideProgress, showProgress],
  );

  // Leave exam unmounts the screen while confirm() is still pending; settle it
  // as "stay" so nothing awaits a dialog that no longer exists.
  useEffect(() => () => {
    resolverRef.current?.(false);
    resolverRef.current = null;
    setOptions(null);
    setProgress(null);
    setConfirmPhase('idle');
  }, []);

  return (
    <MeetingConfirmContext.Provider value={value}>
      <Box flex={1} position="relative">
        {children}
        {options ? (
          <MeetingScrim testID={options.overlayTestID ?? 'meeting-confirm'}>
            <VStack
              bg="$bgCard"
              borderRadius="$lg"
              p="$5"
              space="md"
              maxWidth={420}
              width="90%"
              softShadow="3"
            >
              <Heading size="md">{options.title}</Heading>
              {options.message ? (
                <Text size="sm" color="$textSubtle" testID={options.messageTestID}>
                  {options.message}
                </Text>
              ) : null}
              {confirmPhase === 'error' && options.confirmErrorMessage ? (
                <Text size="sm" color="$statusError" testID={options.confirmErrorTestID}>
                  {options.confirmErrorMessage}
                </Text>
              ) : null}
              <HStack space="md" justifyContent="flex-end">
                <Button
                  variant="outline"
                  action="secondary"
                  onPress={() => settle(false)}
                  testID={options.cancelTestID ?? 'meeting-confirm-cancel'}
                >
                  <ButtonText>{options.cancelLabel ?? t('common.buttons.cancel')}</ButtonText>
                </Button>
                <Button
                  action={options.destructive ? 'negative' : 'primary'}
                  onPress={() => {
                    handleConfirmPress().catch(() => {});
                  }}
                  testID={
                    confirmPhase === 'error' && options.confirmAnywayTestID
                      ? options.confirmAnywayTestID
                      : (options.confirmTestID ?? 'meeting-confirm-accept')
                  }
                >
                  <ButtonText>
                    {confirmPhase === 'error' && options.confirmAnywayLabel
                      ? options.confirmAnywayLabel
                      : options.confirmLabel}
                  </ButtonText>
                </Button>
              </HStack>
            </VStack>
          </MeetingScrim>
        ) : null}
        {progress && !options ? (
          <MeetingScrim testID="oral-exam-transition-progress-layer">
            <VStack
              bg="$bgCard"
              borderRadius="$lg"
              p="$5"
              space="md"
              maxWidth={420}
              width="90%"
              softShadow="3"
              testID="oral-exam-transition-progress-dialog"
            >
              <HStack space="sm" alignItems="center">
                {progress.pending ? (
                  <Spinner size="small" testID="oral-exam-transition-progress-spinner" />
                ) : null}
                <Heading size="md" color="$textHeading">{progress.title}</Heading>
              </HStack>
              <VStack space="sm">
                {progress.lines.map((line) => (
                  <Text
                    key={line}
                    size="sm"
                    color={line === progress.lines[progress.lines.length - 1] ? '$textSubtle' : '$textBody'}
                  >
                    {line}
                  </Text>
                ))}
                {progress.error ? (
                  <Text size="sm" color="$statusError" testID="oral-exam-transition-progress-error">
                    {progress.error}
                  </Text>
                ) : null}
              </VStack>
              {progress.pending ? null : (
                <HStack space="sm" justifyContent="flex-end" w="$full">
                  <Button
                    variant="outline"
                    onPress={progress.onClose}
                    testID="oral-exam-transition-progress-close"
                  >
                    <ButtonText>{t('common.buttons.close', { defaultValue: 'Close' })}</ButtonText>
                  </Button>
                  {progress.error && progress.onRetry ? (
                    <Button onPress={progress.onRetry} testID="oral-exam-transition-progress-retry">
                      <ButtonText>{t('common.buttons.retry', { defaultValue: 'Retry' })}</ButtonText>
                    </Button>
                  ) : null}
                </HStack>
              )}
            </VStack>
          </MeetingScrim>
        ) : null}
      </Box>
    </MeetingConfirmContext.Provider>
  );
}
